import type {
  CellDataType,
  ColumnDataType,
  SerializedCells,
} from '@blocksuite/affine-model';
import type {
  BlockSnapshot,
  DeltaInsert,
  DocSnapshot,
  Store,
  Transformer,
} from '@blocksuite/store';
import {
  DATABASE_LARGE_DATA_POLICY,
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseShardManifest,
  type DatabaseShardManifestEntry,
  normalizeDatabaseShardManifest,
} from '@streamlain/database-identity';

import { databaseBlockModels } from '../properties/model';

function calculateColumnWidths(rows: string[][]): number[] {
  return (
    rows[0]?.map((_, colIndex) =>
      Math.max(...rows.map(row => (row[colIndex] || '').length))
    ) ?? []
  );
}

function formatRow(
  row: string[],
  columnWidths: number[],
  isHeader: boolean
): string {
  const cells = row.map((cell, colIndex) =>
    cell?.padEnd(columnWidths[colIndex] ?? 0, ' ')
  );
  const rowString = `| ${cells.join(' | ')} |`;
  return isHeader
    ? `${rowString}\n${formatSeparator(columnWidths)}`
    : rowString;
}

function formatSeparator(columnWidths: number[]): string {
  const separator = columnWidths.map(width => '-'.repeat(width)).join(' | ');
  return `| ${separator} |`;
}

export function formatTable(rows: string[][]): string {
  const columnWidths = calculateColumnWidths(rows);
  const formattedRows = rows.map((row, index) =>
    formatRow(row, columnWidths, index === 0)
  );
  return formattedRows.join('\n');
}
export const isDelta = (value: unknown): value is { delta: DeltaInsert[] } => {
  if (typeof value === 'object' && value !== null) {
    return '$blocksuite:internal:text$' in value;
  }
  return false;
};
type Table = {
  headers: ColumnDataType[];
  rows: Row[];
};
type Row = {
  cells: Cell[];
};
type Cell = {
  value: string | { delta: DeltaInsert[] };
};
type DatabaseSnapshotProps = {
  databaseId?: unknown;
  databaseRootDocId?: unknown;
  databaseShardManifest?: unknown;
  'prop:databaseId'?: unknown;
  'prop:databaseRootDocId'?: unknown;
  'prop:databaseShardManifest'?: unknown;
  columns?: unknown;
};

type DatabaseTableExportContext = Pick<
  Transformer,
  'docCRUD' | 'docToSnapshot'
>;

type ReadonlyMapLike = {
  forEach(callback: (value: unknown, key: string) => void): void;
  get(key: string): unknown;
  readonly size: number;
};

type ReadonlyArrayLike = {
  toArray(): unknown[];
  readonly length: number;
};

export function assertDatabaseSnapshotTableExportable(
  props?: DatabaseSnapshotProps
): void {
  if (!props) return;
  const manifest =
    props.databaseShardManifest ?? props['prop:databaseShardManifest'];
  if (manifest) {
    throw new Error(
      'Row-sharded database snapshots require the bounded database export job'
    );
  }
}

export const processTable = (
  columns: ColumnDataType[],
  children: BlockSnapshot[],
  cells: SerializedCells,
  props?: DatabaseSnapshotProps
): Table => {
  assertDatabaseSnapshotTableExportable(props);

  const table: Table = {
    headers: columns,
    rows: [],
  };
  children.forEach(v => {
    const row: Row = {
      cells: [],
    };
    const title = v.props.text;
    if (isDelta(title)) {
      row.cells.push({
        value: title,
      });
    } else {
      row.cells.push({
        value: '',
      });
    }

    columns.forEach(col => {
      const property = databaseBlockModels[col.type];
      const cell = cells[v.id]?.[col.id];
      if (col.type === 'title') {
        return;
      }
      if (!cell || !property) {
        row.cells.push({
          value: '',
        });
        return;
      }
      let value: string | { delta: DeltaInsert[] };
      if (isDelta(cell.value)) {
        value = cell.value;
      } else {
        value = property.config.rawValue.toString({
          value: cell.value,
          data: col.data,
        });
      }
      row.cells.push({
        value,
      });
    });
    table.rows.push(row);
  });

  return table;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isMapLike(value: unknown): value is ReadonlyMapLike {
  return (
    isRecord(value) &&
    typeof value.get === 'function' &&
    typeof value.forEach === 'function' &&
    typeof value.size === 'number'
  );
}

function isArrayLike(value: unknown): value is ReadonlyArrayLike {
  return (
    isRecord(value) &&
    typeof value.toArray === 'function' &&
    typeof value.length === 'number'
  );
}

function readStringProp(
  props: DatabaseSnapshotProps | undefined,
  key: keyof DatabaseSnapshotProps,
  legacyKey: keyof DatabaseSnapshotProps
): string | null {
  const value = props?.[key] ?? props?.[legacyKey];
  return typeof value === 'string' && value ? value : null;
}

function readManifest(
  props?: DatabaseSnapshotProps
): DatabaseShardManifest | undefined {
  const value =
    props?.databaseShardManifest ?? props?.['prop:databaseShardManifest'];
  return value ? normalizeDatabaseShardManifest(value) : undefined;
}

function readColumns(
  props?: DatabaseSnapshotProps
): ColumnDataType[] | undefined {
  return Array.isArray(props?.columns)
    ? (props.columns as ColumnDataType[])
    : undefined;
}

function findDatabaseSnapshotBlock(
  snapshot: BlockSnapshot,
  databaseId: string | null
): BlockSnapshot | null {
  if (
    snapshot.flavour === 'affine:database' &&
    (!databaseId ||
      snapshot.id === databaseId ||
      snapshot.props.databaseId === databaseId ||
      snapshot.props['prop:databaseId'] === databaseId)
  ) {
    return snapshot;
  }

  for (const child of snapshot.children) {
    const found = findDatabaseSnapshotBlock(child, databaseId);
    if (found) {
      return found;
    }
  }
  return null;
}

function resolveDatabaseRootBlockSnapshot(
  props: DatabaseSnapshotProps | undefined,
  context: DatabaseTableExportContext
): BlockSnapshot | null {
  const rootDocId = readStringProp(
    props,
    'databaseRootDocId',
    'prop:databaseRootDocId'
  );
  if (!rootDocId) {
    return null;
  }
  const rootStore = context.docCRUD.get(rootDocId);
  if (!rootStore) {
    throw new Error(
      `Cannot export row-sharded database because root document "${rootDocId}" is not available.`
    );
  }

  const rootSnapshot = context.docToSnapshot(rootStore) as DocSnapshot | null;
  if (!rootSnapshot) {
    throw new Error(
      `Cannot export row-sharded database because root document "${rootDocId}" could not be snapshotted.`
    );
  }

  const databaseId = readStringProp(props, 'databaseId', 'prop:databaseId');
  const rootBlock = findDatabaseSnapshotBlock(rootSnapshot.blocks, databaseId);
  if (!rootBlock) {
    throw new Error(
      `Cannot export row-sharded database because root document "${rootDocId}" has no database block.`
    );
  }
  return rootBlock;
}

function resolveShardedTableSource(
  columns: ColumnDataType[],
  props: DatabaseSnapshotProps | undefined,
  context: DatabaseTableExportContext
): {
  columns: ColumnDataType[];
  manifest: DatabaseShardManifest | undefined;
} {
  const rootBlock = resolveDatabaseRootBlockSnapshot(props, context);
  const rootProps = rootBlock?.props as DatabaseSnapshotProps | undefined;
  return {
    columns: columns.length > 0 ? columns : (readColumns(rootProps) ?? columns),
    manifest: readManifest(props) ?? readManifest(rootProps),
  };
}

function getManifestRowCount(manifest: DatabaseShardManifest): number {
  return manifest.shards.reduce((sum, shard) => sum + shard.rowCount, 0);
}

function assertShardedTableExportWithinBounds(
  manifest: DatabaseShardManifest
): void {
  const rowCount = getManifestRowCount(manifest);
  const rowLimit = DATABASE_LARGE_DATA_POLICY.jobs.exportBatchRowLimit;
  if (rowCount > rowLimit) {
    throw new Error(
      `Row-sharded database table export is limited to ${rowLimit} rows; use the bounded database export job for ${rowCount} rows.`
    );
  }
}

function getShardCandidateDocIds(shard: DatabaseShardManifestEntry): string[] {
  return [shard.serverDocId];
}

function getShardStore(
  manifest: DatabaseShardManifest,
  shard: DatabaseShardManifestEntry,
  context: DatabaseTableExportContext
): { docId: string; store: Store } {
  for (const docId of getShardCandidateDocIds(shard)) {
    const store = context.docCRUD.get(docId);
    if (store) {
      return { docId, store };
    }
  }
  throw new Error(
    `Cannot export sharded database "${manifest.databaseId}" because row shard "${shard.shardId}" is not available.`
  );
}

function readShardRowIds(
  docId: string,
  rowsMap: ReadonlyMapLike,
  rowOrder: ReadonlyArrayLike
): string[] {
  const rowIds = rowOrder.toArray().map(rowId => String(rowId));
  if (rowsMap.size > 0 && rowIds.length === 0) {
    throw new Error(
      `Database row shard "${docId}" is missing ${DATABASE_ROW_SHARD_ORDER_ARRAY_KEY}.`
    );
  }

  const uniqueRowIds: string[] = [];
  const seenRowIds = new Set<string>();
  for (const rowId of rowIds) {
    if (seenRowIds.has(rowId)) {
      continue;
    }
    seenRowIds.add(rowId);
    uniqueRowIds.push(rowId);
  }
  return uniqueRowIds;
}

function getShardRowOrder(store: Store): ReadonlyArrayLike {
  const rowOrder = store.spaceDoc.getArray(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY);
  if (rowOrder && isArrayLike(rowOrder)) {
    return rowOrder;
  }
  throw new Error(
    `Database row shard "${store.id}" is missing ${DATABASE_ROW_SHARD_ORDER_ARRAY_KEY}.`
  );
}

function getShardRowsMap(store: Store): ReadonlyMapLike {
  const rowsMap = store.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  if (!isMapLike(rowsMap)) {
    throw new Error(
      `Database row shard "${store.id}" is missing ${DATABASE_ROW_SHARD_ROWS_MAP_KEY}.`
    );
  }
  return rowsMap;
}

function normalizeExportCell(
  columnId: string,
  rawCell: unknown
): CellDataType | null {
  if (rawCell == null) {
    return null;
  }
  if (isRecord(rawCell) && 'value' in rawCell) {
    return {
      columnId:
        typeof rawCell.columnId === 'string' ? rawCell.columnId : columnId,
      value: rawCell.value,
    };
  }
  return { columnId, value: rawCell };
}

function cellToExportValue(
  column: ColumnDataType,
  cell: CellDataType | null
): string | { delta: DeltaInsert[] } {
  if (!cell) {
    return '';
  }
  if (isDelta(cell.value)) {
    return cell.value;
  }
  const property = databaseBlockModels[column.type];
  if (!property) {
    return cell.value == null ? '' : String(cell.value);
  }
  return property.config.rawValue.toString({
    value: cell.value,
    data: column.data,
  });
}

function rowMapToTableRow(
  columns: ColumnDataType[],
  rowMap: ReadonlyMapLike
): Row {
  return {
    cells: columns
      .map(column =>
        cellToExportValue(
          column,
          normalizeExportCell(column.id, rowMap.get(column.id))
        )
      )
      .map(value => ({ value })),
  };
}

function readShardedRows(
  columns: ColumnDataType[],
  manifest: DatabaseShardManifest,
  context: DatabaseTableExportContext
): Row[] {
  const rows: Row[] = [];
  for (const shard of manifest.shards) {
    const { docId, store } = getShardStore(manifest, shard, context);
    const rowsMap = getShardRowsMap(store);
    const rowOrder = getShardRowOrder(store);
    for (const rowId of readShardRowIds(docId, rowsMap, rowOrder)) {
      const rawCells = rowsMap.get(rowId);
      if (!isMapLike(rawCells)) {
        continue;
      }
      rows.push(rowMapToTableRow(columns, rawCells));
    }
  }
  return rows;
}

export async function processTableForExport(
  columns: ColumnDataType[],
  children: BlockSnapshot[],
  cells: SerializedCells,
  props: DatabaseSnapshotProps | undefined,
  context: DatabaseTableExportContext
): Promise<Table> {
  const source = resolveShardedTableSource(columns, props, context);
  if (!source.manifest) {
    return processTable(source.columns, children, cells, props);
  }

  assertShardedTableExportWithinBounds(source.manifest);
  return {
    headers: source.columns,
    rows: readShardedRows(source.columns, source.manifest, context),
  };
}
